import React, { useState, useEffect } from 'react';
import axios from 'axios';
import UserManagementTable from './UserManagementTable';
import UserFormModal from './UserFormModal';

interface Pool {
  id: string;
  name: string;
}

export interface CognitoUser {
  username: string;
  email: string | null;
  name: string | null;
  status: string;
  enabled: boolean;
  created_at: string | null;
  modified_at: string | null;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000');

const UserManagement: React.FC = () => {
  const [pools, setPools] = useState<Pool[]>([]);
  const [selectedPoolId, setSelectedPoolId] = useState<string | null>(null);
  const [users, setUsers] = useState<CognitoUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [nextToken, setNextToken] = useState<string | null>(null);

  useEffect(() => {
    fetchPools();
  }, []);

  useEffect(() => {
    if (selectedPoolId) {
      fetchUsers(selectedPoolId);
    }
  }, [selectedPoolId]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchPools = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/admin/pools`, {
        headers: getAuthHeaders()
      });
      setPools(response.data);
      if (response.data.length > 0) {
        setSelectedPoolId(response.data[0].id);
      }
      setError(null);
    } catch (err: any) {
      console.error('Error fetching pools:', err);
      setError(err.response?.data?.detail || 'Failed to load user pools');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async (poolId: string, token?: string | null) => {
    try {
      if (token) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setUsers([]);
      }

      const params = new URLSearchParams();
      if (token) params.append('next_token', token);

      const response = await axios.get(
        `${API_BASE_URL}/api/admin/pools/${poolId}/users${params.toString() ? '?' + params.toString() : ''}`,
        { headers: getAuthHeaders() }
      );

      if (token) {
        setUsers(prev => [...prev, ...response.data.users]);
      } else {
        setUsers(response.data.users);
      }
      setNextToken(response.data.next_token || null);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching users:', err);
      setError(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (selectedPoolId && nextToken) {
      fetchUsers(selectedPoolId, nextToken);
    }
  };

  const handleCreateUser = async (email: string, name: string, sendWelcomeEmail: boolean) => {
    if (!selectedPoolId) return;

    try {
      await axios.post(
        `${API_BASE_URL}/api/admin/pools/${selectedPoolId}/users`,
        { email, name, send_welcome_email: sendWelcomeEmail },
        { headers: getAuthHeaders() }
      );
      setShowCreateModal(false);
      fetchUsers(selectedPoolId);
    } catch (err: any) {
      console.error('Error creating user:', err);
      throw new Error(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleEnableUser = async (username: string) => {
    if (!selectedPoolId) return;

    try {
      await axios.post(
        `${API_BASE_URL}/api/admin/pools/${selectedPoolId}/users/${encodeURIComponent(username)}/enable`,
        {},
        { headers: getAuthHeaders() }
      );
      fetchUsers(selectedPoolId);
    } catch (err: any) {
      console.error('Error enabling user:', err);
      alert(err.response?.data?.detail || 'Failed to enable user');
    }
  };

  const handleDisableUser = async (username: string) => {
    if (!selectedPoolId) return;

    try {
      await axios.post(
        `${API_BASE_URL}/api/admin/pools/${selectedPoolId}/users/${encodeURIComponent(username)}/disable`,
        {},
        { headers: getAuthHeaders() }
      );
      fetchUsers(selectedPoolId);
    } catch (err: any) {
      console.error('Error disabling user:', err);
      alert(err.response?.data?.detail || 'Failed to disable user');
    }
  };

  const handleDeleteUser = async (username: string) => {
    if (!selectedPoolId) return;

    if (!window.confirm(`Are you sure you want to delete user "${username}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(
        `${API_BASE_URL}/api/admin/pools/${selectedPoolId}/users/${encodeURIComponent(username)}`,
        { headers: getAuthHeaders() }
      );
      fetchUsers(selectedPoolId);
    } catch (err: any) {
      console.error('Error deleting user:', err);
      alert(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const filteredUsers = users.filter(user => {
    const term = searchTerm.toLowerCase();
    return (
      user.username.toLowerCase().includes(term) ||
      (user.email && user.email.toLowerCase().includes(term)) ||
      (user.name && user.name.toLowerCase().includes(term))
    );
  });

  if (loading && pools.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '1.25rem', color: '#374151' }}>Loading user management...</div>
      </div>
    );
  }

  if (error && pools.length === 0) {
    return (
      <div style={{ padding: '2rem' }}>
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '0.5rem',
          padding: '1rem',
          color: '#991b1b'
        }}>
          <strong>Error:</strong> {error}
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: 'white', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
      <div style={{ padding: '1.5rem', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', margin: 0 }}>
            User Management
          </h2>
          <button
            onClick={() => setShowCreateModal(true)}
            disabled={!selectedPoolId}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: selectedPoolId ? '#2563eb' : '#9ca3af',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: selectedPoolId ? 'pointer' : 'not-allowed',
              fontSize: '0.875rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            + Create User
          </button>
        </div>

        {/* Pool Sub-tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          {pools.map((pool) => (
            <button
              key={pool.id}
              onClick={() => setSelectedPoolId(pool.id)}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: selectedPoolId === pool.id ? '#2563eb' : '#f3f4f6',
                color: selectedPoolId === pool.id ? 'white' : '#374151',
                border: selectedPoolId === pool.id ? 'none' : '1px solid #d1d5db',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
            >
              {pool.name}
            </button>
          ))}
        </div>

        {/* Search */}
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            placeholder="Search users by email or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              paddingLeft: '2.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              boxSizing: 'border-box'
            }}
          />
          <span style={{
            position: 'absolute',
            left: '0.75rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#9ca3af'
          }}>
            🔍
          </span>
        </div>
      </div>

      {/* User Table */}
      <div style={{ padding: '1rem' }}>
        {/* User count */}
        {!loading && !error && (
          <div style={{ marginBottom: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
            Showing {filteredUsers.length} of {users.length} users
            {nextToken && ' (more available)'}
          </div>
        )}

        {/* Scrollable table container */}
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
              Loading users...
            </div>
          ) : error ? (
            <div style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '0.5rem',
              padding: '1rem',
              color: '#991b1b'
            }}>
              {error}
            </div>
          ) : (
            <UserManagementTable
              users={filteredUsers}
              onEnable={handleEnableUser}
              onDisable={handleDisableUser}
              onDelete={handleDeleteUser}
            />
          )}
        </div>

        {/* Load More button */}
        {nextToken && !loading && (
          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              style={{
                padding: '0.5rem 1.5rem',
                backgroundColor: loadingMore ? '#9ca3af' : '#f3f4f6',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                cursor: loadingMore ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              {loadingMore ? 'Loading...' : 'Load More Users'}
            </button>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <UserFormModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateUser}
        />
      )}
    </div>
  );
};

export default UserManagement;
